import React from 'react';
import { Helmet } from 'react-helmet';
import AppStoreScreenshot from './assets/app-store-screenshot.png';
import AskPicsIcon from './assets/askpics-icon.jpg';
import Shot1 from './assets/app-store-screenshots/ap-1.png';
import Shot2 from './assets/app-store-screenshots/ap-2.png';
import Shot3 from './assets/app-store-screenshots/ap-3.png';
import Shot4 from './assets/app-store-screenshots/ap-4.png';
import Shot5 from './assets/app-store-screenshots/ap-5.png';
import './index.css';

const TICKER_ITEMS = [
  'askpics.app',
  'Last deploy 2026.05.12',
  'iOS 17 · 18 · 26',
  'App Store · productivity',
  'Search any screenshot — instantly',
  'Find anything in your screenshots',
  'On-device · private',
];

const SHOWCASE = [
  {
    id: 1,
    tone: 'cream',
    eyebrow: 'Just ask',
    title: ['Your screenshots,', 'searchable.'],
    accentIndex: 1,
    lede: 'Type a thought the way you\'d say it out loud. AskPics surfaces the screenshots that match — by what they show, what they say, and what you saved them for.',
    pills: [
      { text: '"Paris hotels I shortlisted last summer"', tone: 'dark' },
      { text: '3 hotels from your camera roll — Le Pigalle, Hôtel Grand Amour, Le Bristol. Saved Jun 3–18.', tone: 'light' },
    ],
    image: Shot1,
    align: 'right',
  },
  {
    id: 2,
    tone: 'cream',
    eyebrow: 'Ask in plain English',
    title: ['Chat with', 'what you saved.'],
    accentIndex: 1,
    lede: 'No folders. No tags. Just ask — AskPics reads the pixels and the text in every screenshot you\'ve ever saved.',
    pills: [
      { text: '"dinner ideas from last week"', tone: 'dark' },
      { text: '"that blue dress I saved"', tone: 'light' },
      { text: '"that Didion quote"', tone: 'tan' },
    ],
    image: Shot2,
    align: 'left',
  },
  {
    id: 3,
    tone: 'amber',
    eyebrow: 'Auto-categorized',
    title: ['Sorted the', 'instant you', 'save it.'],
    accentIndex: 2,
    lede: 'Recipes, receipts, outfits, travel, quotes, tweets — AskPics sorts everything you upload into the categories you care about.',
    pills: [
      { text: 'Recipes · 42', tone: 'light' },
      { text: 'Quotes · 23', tone: 'light' },
      { text: 'Travel · 18', tone: 'dark' },
      { text: 'Outfits · 29', tone: 'light' },
      { text: 'Tweets · 31', tone: 'light' },
    ],
    image: Shot3,
    align: 'right',
  },
  {
    id: 4,
    tone: 'ink',
    eyebrow: 'Indexed in seconds',
    title: ['Every pixel,', 'every line —', 'found.'],
    accentIndex: 2,
    lede: 'On-device OCR plus vision models. 1,847 screenshots? Indexed in under a minute.',
    pills: [
      { text: '42 / 42 indexed · 0:47', tone: 'outline' },
      { text: 'pixels + text', tone: 'light' },
      { text: 'on-device', tone: 'tan' },
    ],
    image: Shot4,
    align: 'left',
  },
  {
    id: 5,
    tone: 'sage',
    eyebrow: 'Private by design',
    title: ['Your screenshots', 'stay yours.'],
    accentIndex: 1,
    lede: 'Screenshots only — never selfies, never full-res photos. Revoke access anytime in iOS Settings.',
    pills: [
      { text: '🔒  Screenshots only', tone: 'light' },
      { text: '✓  Revoke anytime', tone: 'dark' },
    ],
    image: Shot5,
    align: 'right',
  },
];

const App = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'AskPics — Search Screenshots on iPhone',
    description:
      'AskPics is an iPhone app that searches your screenshots with AI. Type a thought, find what you saved — recipes, outfits, quotes, travel, receipts.',
    url: 'https://www.askpics.app',
    mainEntity: {
      '@type': 'MobileApplication',
      name: 'AskPics — Search Screenshots',
      operatingSystem: 'iOS',
      applicationCategory: 'ProductivityApplication',
      downloadUrl:
        'https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139',
      url: 'https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139',
      description:
        'Search your screenshots in seconds with AI and find saved information instantly.',
    },
  };

  // Duplicate ticker items for seamless loop
  const tickerItems = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="rg-page">
      <Helmet>
        <title>AskPics — Search Screenshots on iPhone</title>
        <meta
          name="description"
          content="AskPics is an iPhone app that searches your screenshots with AI. Find recipes, outfits, quotes, travel notes, receipts — anything you ever screenshotted, instantly."
        />
        <meta
          name="keywords"
          content="AskPics, search screenshots, screenshot search app, iPhone screenshot search, screenshot AI, find screenshots, ask your screenshots"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AskPics — Search Screenshots on iPhone" />
        <meta
          property="og:description"
          content="Search your screenshots with AI. Find anything you saved — instantly. Download AskPics on the App Store."
        />
        <meta property="og:url" content="https://www.askpics.app" />
        <meta property="og:image" content="https://www.askpics.app/og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="AskPics — your screenshots, searchable." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AskPics — Search Screenshots on iPhone" />
        <meta
          name="twitter:description"
          content="Search your screenshots with AI. Find anything you saved — instantly. Download AskPics on the App Store."
        />
        <meta name="twitter:image" content="https://www.askpics.app/og.png" />
        <meta name="twitter:image:alt" content="AskPics — your screenshots, searchable." />
        <meta name="apple-itunes-app" content="app-id=6756801139" />
        <link rel="canonical" href="https://www.askpics.app" />
        <script type="application/ld+json">{JSON.stringify(seoJsonLd)}</script>
      </Helmet>

      {/* Ticker bar */}
      <div className="rg-ticker" aria-hidden="true">
        <div className="rg-ticker__status">
          <span className="rg-ticker__dot"></span>
          <span>Live · iOS</span>
        </div>
        <div className="rg-ticker__track">
          <div className="rg-ticker__scroll">
            {tickerItems.map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        </div>
        <div className="rg-ticker__meta">NYC · 40.7128°N</div>
      </div>

      {/* Nav */}
      <header className="rg-nav">
        <a href="/" className="rg-brand" aria-label="AskPics home">
          <img src={AskPicsIcon} alt="" className="rg-brand__askpics-icon" aria-hidden="true" />
          <span className="rg-brand__word">AskPics</span>
        </a>
        <div className="rg-nav__meta" aria-hidden="true">
          <span className="rg-nav__stamp">LAUNCH · 2026</span>
          <span className="rg-nav__sep"></span>
          <span>iOS / APP STORE</span>
        </div>
      </header>

      {/* Hero */}
      <section className="rg-hero">
        <div className="rg-hero__copy">
          <div className="rg-eyebrow">
            <span className="rg-eyebrow__box"></span>
            <span>Live on the App Store</span>
            <span className="rg-eyebrow__div"></span>
            <span>iOS · 001</span>
          </div>

          <h1 className="rg-hero__title" aria-label="Search your screenshots.">
            <span className="rg-word rg-word--1">Search</span>
            {' '}
            <span className="rg-word rg-word--2">your</span>
            {' '}
            <span className="rg-word rg-word--3">screenshots.</span>
          </h1>

          <p className="rg-lede">
            <strong>AskPics — Search Screenshots</strong> is an iPhone app for
            finding anything you've ever saved. Type a thought; AskPics surfaces
            the screenshot — instantly.
          </p>

          <div className="rg-cta-row">
            <a
              className="rg-app-store-btn"
              href="https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download AskPics on the App Store"
            >
              <svg className="rg-app-store-btn__apple" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="rg-app-store-btn__labels">
                <span className="rg-app-store-btn__tiny">Download on the</span>
                <span className="rg-app-store-btn__big">App Store</span>
              </span>
            </a>
            <a
              className="rg-secondary-link"
              href="https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139"
              target="_blank"
              rel="noopener noreferrer"
            >
              askpics.app
              <span className="rg-secondary-link__arrow">↗</span>
            </a>
          </div>

          <div className="rg-hero__meta">
            <div>
              <div className="rg-hero__meta-key">App</div>
              <div className="rg-hero__meta-val">AskPics<span className="rg-em"> — Search Screenshots</span></div>
            </div>
            <div>
              <div className="rg-hero__meta-key">Platform</div>
              <div className="rg-hero__meta-val"><span className="rg-em">iPhone ·</span> iOS</div>
            </div>
            <div>
              <div className="rg-hero__meta-key">Status</div>
              <div className="rg-hero__meta-val">Shipping <span className="rg-em">stably</span></div>
            </div>
          </div>
        </div>

        {/* Stage */}
        <div className="rg-stage">
          <div className="rg-stage__pill">
            <span className="rg-stage__pill-dot"></span>
            Live on App Store
          </div>

          <a
            className="rg-device-wrap"
            href="https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View AskPics on the App Store"
          >
            <div className="rg-device">
              <div className="rg-screen">
                <div className="rg-screen__scroll">
                  <img src={AppStoreScreenshot} alt="AskPics on the App Store" />
                </div>
                <div className="rg-screen__fade"></div>
                <div className="rg-screen__hint">Tap to open in the App Store</div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Section divider */}
      <div className="rg-divider" aria-hidden="true">
        <span className="rg-divider__lab">What it does</span>
        <span className="rg-divider__line"></span>
        <span className="rg-divider__count">5 / FEATURES</span>
      </div>

      {/* Showcase */}
      <section className="rg-showcase" aria-label="AskPics features">
        {SHOWCASE.map((s, idx) => (
          <article
            key={s.id}
            className={`rg-show rg-show--${s.tone} rg-show--${s.align}`}
          >
            <div className="rg-show__copy">
              <div className="rg-show__eyebrow">
                <span className="rg-show__num">0{s.id}</span>
                <span>{s.eyebrow}</span>
              </div>
              <h2 className="rg-show__title">
                {s.title.map((line, i) => (
                  <span key={i} className="rg-show__line">
                    {i === s.accentIndex ? <em>{line}</em> : line}
                    {i < s.title.length - 1 && <br />}
                  </span>
                ))}
              </h2>
              <p className="rg-show__lede">{s.lede}</p>
              <ul className="rg-show__pills">
                {s.pills.map((p, i) => (
                  <li key={i} className={`rg-pill rg-pill--${p.tone}`}>{p.text}</li>
                ))}
              </ul>
              <div className="rg-show__index" aria-hidden="true">
                <span>FIG · 0{s.id}</span>
                <span className="rg-show__index-bar"></span>
                <span>{idx === SHOWCASE.length - 1 ? 'END' : 'NEXT ↓'}</span>
              </div>
            </div>
            <div className="rg-show__shot">
              <div className="rg-show__shot-frame">
                <img src={s.image} alt={`AskPics — ${s.title.join(' ')}`} loading="lazy" />
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Closing CTA */}
      <section className="rg-closing" aria-label="Get AskPics">
        <div className="rg-closing__top">
          <span className="rg-closing__lab">Available now</span>
          <span className="rg-closing__line"></span>
          <span className="rg-closing__lab">App Store · iPhone</span>
        </div>
        <h2 className="rg-closing__title">
          Find <em>anything</em> in <br />your screenshots.
        </h2>
        <p className="rg-closing__lede">
          AskPics ships every week. Try it free on iPhone.
        </p>
        <div className="rg-closing__cta">
          <a
            className="rg-app-store-btn"
            href="https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download AskPics on the App Store"
          >
            <svg className="rg-app-store-btn__apple" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span className="rg-app-store-btn__labels">
              <span className="rg-app-store-btn__tiny">Download on the</span>
              <span className="rg-app-store-btn__big">App Store</span>
            </span>
          </a>
          <a
            className="rg-secondary-link"
            href="https://apps.apple.com/us/app/askpics-search-screenshots/id6756801139"
            target="_blank"
            rel="noopener noreferrer"
          >
            askpics.app
            <span className="rg-secondary-link__arrow">↗</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="rg-footer">
        <div className="rg-footer__left">
          <div className="rg-footer__copy">text + images + code © ragobble 2026 · all rights reserved</div>
        </div>
        <div className="rg-footer__links">
          <a href="/privacy">Privacy Policy</a>
          <a href="/support">Support</a>
        </div>
      </footer>
    </div>
  );
};

export default App;
